import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 4;
const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
const LAST_PAGE = false;
const BG_ID = 4;
const BG_TYPE = "jpg";

const USE_CASE = "USE CASE 1";
const LEFT_TITLE = "Integrated Command Centre\nfor IT Landscape";
const LEFT_LIST_ITEM_1 = "Full stack observability";
const LEFT_LIST_ITEM_2 = "Noise suppression through correlation and de-\nduplication of alerts and proactive anomaly\ndetection";
const LEFT_LIST_ITEM_3 = "Cognitive automation and bot repository for AI led\nservice automation";
const LEFT_LIST_ITEM_4 = "Aligns IT operations with business outcomes in\nbusiness control view ";

// Video player component
const VideoPlayer = () => {
  return (
    <div
      style={{
        position: "relative",
        top: "2.5%",
        left: "6.5%",
        width: "92%", // Adjust as needed
        height: "auto",
        aspectRatio: "auto", // Ensures the container matches the video's aspect ratio
        overflow: "visible",
        zIndex: 9999,
      }}
    >
      {/* Video Player */}
      <video
        src={`${Config.BASE_URL}/images/videos/video_1.mp4`}
        autoPlay
        muted
        controls
        style={{
          position: "absolute",
          zIndex: 10,
          left: "var(--width-42)",
          top: "var(--height-36)",
          width: "var(--width-988)", // Matches the container's width
          height: "auto",
          display: "block",
        }}
      />
      <div style={{ position: "absolute", background: "#fff", width: "var(--width-900)", height: "var(--height-520)" }}></div>
      {/* Frame Overlay */}
      <img
        src={`${Config.BASE_URL}/images/videos/frame_1.png`}
        alt="Video Frame"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "var(--width-1035)", // Matches the container's width
          height: "auto", // Matches the container's height
          objectFit: "fill", // Ensures the frame matches the container dimensions exactly
          pointerEvents: "none", // Ensures it doesn’t block interactions with the video
        }}
      />
    </div>
  );
};

interface SliderWrapperProps {
  children: React.ReactNode;
}
const SliderWrapper = ({ children }: SliderWrapperProps) => (
  <div
    style={{
      position: "relative",
      overflowX: "hidden",
      width: "100%", // Ensures the wrapper fills the screen
    }}
  >
    {children}
  </div>
);

// Image slider component remains unchanged
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]);

  const images = [1, 2, 3, 4];
  const GAP = "var(--width-57)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        bottom: "4%", // Position above buttons
        left: "0", // Start from left side
        width: "112%", // Cover most of the width
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        overflow: "hidden", // Prevent overflow scrollbars
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginRight: i < images.length - 1 ? GAP : 0,
            width: "15%",
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "-100%"})`,
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Define keyframes for animations
  const keyframes = `
    @keyframes gradientMove {
      0% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }
    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.03);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes shadowPulse {
      0% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3)
      }
      50% {
        box-shadow: 0 0 55px rgba(255,255,255,0.4);
      }
      100% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3);
      }
    }
  `;

  const leftBeforeTitle = (
    <p
      style={{
        marginBottom: "var(--width-8)",
        color: "#fff",
        fontFamily: "Tungsten",
        fontSize: "var(--width-40)",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "110%",
        textTransform: "uppercase",
        transform: "translateY(-210%)",
      }}
    >
      {USE_CASE}
    </p>
  );

  const leftAfterDescription = (
    <div style={{ transform: "translateY(-25%)", padding: "var(--width-10) 0" }}>
      <style>{keyframes}</style>
      <img
        src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/left_separator.png`}
        alt="separator"
        style={{
          width: "var(--width-605)",
          height: "auto",
        }}
      />
      <div
        style={{
          color: "#fff",
          fontFamily: "Myriad Pro",
          fontSize: "var(--width-24)",
          fontStyle: "normal",
          fontWeight: "380",
          lineHeight: "150%",
          whiteSpace: "pre-line",
        }}
      >
        <ul
          style={{
            marginTop: "var(--width-30)",
            marginBottom: "var(--width-10)",
          }}
        >
          <li>{LEFT_LIST_ITEM_1}</li>
          <li>{LEFT_LIST_ITEM_2}</li>
          <li>{LEFT_LIST_ITEM_3}</li>
          <li>{LEFT_LIST_ITEM_4}</li>
        </ul>
      </div>
    </div>
  );

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
          style: {
            borderRadius: "45px",
            padding: "var(--width-5) var(--width-25)",
            gap: "var(--width-16)",
          },
        }
      : undefined;

  const nextButton = !LAST_PAGE
    ? {
        label: "Next",
        type: "next" as const,
        endIcon: { icon: "arrow-left" },
        onClick: () => setCurrentPage(NEXT_PAGE_ID),
        style: {
          borderRadius: "45px",
          padding: "var(--width-5) var(--width-25)",
          gap: "var(--width-16)",
        },
      }
    : undefined;

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%)",
    title: LEFT_TITLE,
    styleTitle: {
      color: "#fff",
      fontFamily: "Tungsten",
      fontSize: "var(--width-64)",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "110%",
      textTransform: "uppercase",
      transform: "translateY(-65%)",
    },
    leftBeforeTitle: leftBeforeTitle,
    leftAfterDescription: leftAfterDescription,
    // Combine the VideoPlayer and ImageSlider in the right side component
    rightSideComponent: (
      <SliderWrapper>
        {currentPage === PAGE_ID && <VideoPlayer />}
        <ImageSlider key={currentPage} />
      </SliderWrapper>
    ),
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
